<template>
  <woot-modal :show.sync="show" :on-close="onClose">
    <div class="column content-box">
      <woot-modal-header :header-title="$t('EXPORT_CONVERSATIONS.TITLE')">
        <p>{{ $t('EXPORT_CONVERSATIONS.DESC') }}</p>
      </woot-modal-header>
      <div class="row modal-content export-conversations">
        <div class="medium-12 columns">
          <label>
            {{ $t('EXPORT_CONVERSATIONS.FORM.CONTACT.LABEL') }}
            <multiselect
              ref="contactSelect"
              v-model="selectedContact"
              :options="contactSearchResults"
              label="name"
              track-by="id"
              :internal-search="false"
              :close-on-select="true"
              :clear-on-select="false"
              :show-labels="false"
              :loading="isSearchingContacts"
              :placeholder="$t('EXPORT_CONVERSATIONS.FORM.CONTACT.PLACEHOLDER')"
              @search-change="onContactSearch"
              @open="onContactDropdownOpen"
              @select="onContactSelect"
            >
              <template slot="singleLabel" slot-scope="props">
                <contact-dropdown-item
                  :identifier="props.option.id"
                  :name="props.option.name"
                  :email="props.option.email"
                  :phone-number="props.option.phone_number"
                />
              </template>
              <template slot="option" slot-scope="props">
                <contact-dropdown-item
                  :identifier="props.option.id"
                  :name="props.option.name"
                  :email="props.option.email"
                  :phone-number="props.option.phone_number"
                />
              </template>
              <span slot="noResult">
                {{ $t('EXPORT_CONVERSATIONS.FORM.CONTACT.NO_RESULTS') }}
              </span>
            </multiselect>
          </label>

          <div v-if="!allHistory" class="export-conversations__date-range">
            <p class="export-conversations__field-label">
              {{ $t('EXPORT_CONVERSATIONS.FORM.DATE_RANGE.LABEL') }}
            </p>
            <reports-filters-date-range @on-range-change="onDateRangeChange" />
            <woot-date-range-picker
              v-if="isCustomDateRange"
              show-range
              class="no-margin"
              :value="customDateRange"
              :confirm-text="$t('EXPORT_CONVERSATIONS.FORM.DATE_RANGE.CONFIRM')"
              @change="onCustomDateRangeChange"
            />
          </div>

          <div class="export-conversations__option">
            <label class="export-conversations__checkbox-label">
              <input
                v-model="allHistory"
                type="checkbox"
                @change="onFiltersChange"
              />
              <span>{{ $t('EXPORT_CONVERSATIONS.FORM.ALL_HISTORY') }}</span>
            </label>
            <fluent-icon
              v-tooltip.right="{
                content: $t('EXPORT_CONVERSATIONS.FORM.ALL_HISTORY_TOOLTIP'),
                classes: ['export-conversations__tooltip'],
              }"
              size="14"
              icon="info"
              class="export-conversations__info-icon"
            />
          </div>

          <div class="export-conversations__option">
            <label class="export-conversations__checkbox-label">
              <input v-model="excludePrivate" type="checkbox" />
              <span>{{ $t('EXPORT_CONVERSATIONS.FORM.EXCLUDE_PRIVATE') }}</span>
            </label>
            <fluent-icon
              v-tooltip.right="{
                content: $t(
                  'EXPORT_CONVERSATIONS.FORM.EXCLUDE_PRIVATE_TOOLTIP'
                ),
                classes: ['export-conversations__tooltip'],
              }"
              size="14"
              icon="info"
              class="export-conversations__info-icon"
            />
          </div>

          <div v-if="isLoadingEligible" class="export-conversations__state">
            {{ $t('EXPORT_CONVERSATIONS.STATE.LOADING') }}
          </div>
          <div
            v-else-if="hasSearched && !eligibleConversations.length"
            class="export-conversations__state"
          >
            {{ $t('EXPORT_CONVERSATIONS.STATE.EMPTY') }}
          </div>
          <label
            v-else-if="eligibleConversations.length > 1"
            class="export-conversations__list"
          >
            {{ $t('EXPORT_CONVERSATIONS.FORM.CONVERSATIONS_LIST.LABEL') }}
            <multiselect
              v-model="selectedConversations"
              :options="eligibleConversations"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :hide-selected="false"
              :disabled="allHistory"
              :max-height="150"
              track-by="id"
              label="exportLabel"
              :placeholder="
                $t('EXPORT_CONVERSATIONS.FORM.CONVERSATIONS_LIST.PLACEHOLDER')
              "
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
            />
          </label>
        </div>

        <div class="modal-footer">
          <div class="medium-12 columns">
            <woot-button
              :disabled="!canDownload || isDownloading"
              :loading="isDownloading"
              @click="download"
            >
              {{ $t('EXPORT_CONVERSATIONS.FORM.DOWNLOAD') }}
            </woot-button>
            <button class="button clear" @click.prevent="onClose">
              {{ $t('EXPORT_CONVERSATIONS.FORM.CANCEL') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import format from 'date-fns/format';

import ContactAPI from 'dashboard/api/contacts';
import ContactDropdownItem from 'dashboard/modules/contact/components/ContactDropdownItem';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import ReportsFiltersDateRange from '../components/Filters/DateRange.vue';
import { DATE_RANGE_OPTIONS } from '../constants';
import { getUnixStartOfDay, getUnixEndOfDay } from 'shared/helpers/DateHelper';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  components: {
    ContactDropdownItem,
    WootDateRangePicker,
    ReportsFiltersDateRange,
  },
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      selectedContact: null,
      contactSearchResults: [],
      isSearchingContacts: false,
      selectedDateRangeOption: DATE_RANGE_OPTIONS.LAST_7_DAYS,
      customDateRange: [new Date(), new Date()],
      allHistory: false,
      excludePrivate: false,
      eligibleConversations: [],
      selectedConversations: [],
      isLoadingEligible: false,
      isDownloading: false,
      hasSearched: false,
      searchDebounce: null,
      eligibleDebounce: null,
    };
  },
  computed: {
    isCustomDateRange() {
      return (
        this.selectedDateRangeOption.id ===
        DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id
      );
    },
    from() {
      if (this.isCustomDateRange) {
        return getUnixStartOfDay(this.customDateRange[0]);
      }
      return getUnixStartOfDay(
        new Date(
          Date.now() - this.selectedDateRangeOption.offset * 24 * 60 * 60 * 1000
        )
      );
    },
    to() {
      if (this.isCustomDateRange) {
        return getUnixEndOfDay(this.customDateRange[1]);
      }
      return getUnixEndOfDay(new Date());
    },
    canDownload() {
      if (this.isLoadingEligible || !this.eligibleConversations.length) {
        return false;
      }
      if (this.eligibleConversations.length === 1 || this.allHistory) {
        return true;
      }
      return this.selectedConversations.length > 0;
    },
  },
  watch: {
    selectedContact() {
      this.onFiltersChange();
    },
  },
  methods: {
    formatDate(date) {
      return format(new Date(date), 'dd/MM/yyyy HH:mm');
    },
    onContactDropdownOpen() {
      if (!this.contactSearchResults.length && !this.isSearchingContacts) {
        return this.fetchInitialContacts();
      }
      return Promise.resolve();
    },
    onContactSelect() {
      this.$nextTick(() => {
        this.$refs.contactSelect.deactivate();
      });
    },
    async fetchInitialContacts() {
      this.isSearchingContacts = true;
      try {
        const {
          data: { payload },
        } = await ContactAPI.get();
        this.contactSearchResults = payload;
      } catch (error) {
        this.showAlert(this.$t('EXPORT_CONVERSATIONS.FORM.CONTACT.ERROR'));
      } finally {
        this.isSearchingContacts = false;
      }
    },
    onContactSearch(query) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(async () => {
        if (!query) {
          this.fetchInitialContacts();
          return;
        }
        this.isSearchingContacts = true;
        try {
          const {
            data: { payload },
          } = await ContactAPI.search(query);
          this.contactSearchResults = payload;
        } catch (error) {
          this.showAlert(this.$t('EXPORT_CONVERSATIONS.FORM.CONTACT.ERROR'));
        } finally {
          this.isSearchingContacts = false;
        }
      }, 300);
    },
    onDateRangeChange(selectedRange) {
      this.selectedDateRangeOption = selectedRange;
      this.onFiltersChange();
    },
    onCustomDateRangeChange(value) {
      this.customDateRange = value;
      this.onFiltersChange();
    },
    onFiltersChange() {
      this.eligibleConversations = [];
      this.selectedConversations = [];
      this.hasSearched = false;

      if (!this.selectedContact) {
        return;
      }

      clearTimeout(this.eligibleDebounce);
      this.eligibleDebounce = setTimeout(() => this.fetchEligible(), 300);
    },
    conversationLabel(conversation) {
      return this.$t('EXPORT_CONVERSATIONS.FORM.CONVERSATIONS_LIST.ITEM', {
        displayId: conversation.display_id,
        date: this.formatDate(conversation.created_at),
        count: conversation.messages_count,
      });
    },
    async fetchEligible() {
      this.isLoadingEligible = true;
      try {
        const conversations = await this.$store.dispatch(
          'fetchConversationsExportEligible',
          this.buildParams()
        );
        // Every eligible conversation is selected by default - the user can
        // then deselect the ones they don't want (unless "all history" is
        // active, which always exports everything and disables the picker).
        this.eligibleConversations = conversations.map(conversation => ({
          ...conversation,
          exportLabel: this.conversationLabel(conversation),
        }));
        this.selectedConversations = this.eligibleConversations;
      } catch (error) {
        this.showAlert(this.$t('EXPORT_CONVERSATIONS.STATE.ERROR'));
      } finally {
        this.isLoadingEligible = false;
        this.hasSearched = true;
      }
    },
    buildParams() {
      return {
        contactId: this.selectedContact.id,
        from: this.from,
        to: this.to,
        allHistory: this.allHistory,
      };
    },
    exportConversationIds() {
      if (this.allHistory || this.eligibleConversations.length === 1) {
        return this.eligibleConversations.map(conversation => conversation.id);
      }
      return this.selectedConversations.map(conversation => conversation.id);
    },
    async download() {
      this.isDownloading = true;
      try {
        await this.$store.dispatch('downloadConversationsExportPDF', {
          ...this.buildParams(),
          conversationIds: this.exportConversationIds(),
          excludePrivate: this.excludePrivate,
          fileName: `conversations-export-${this.selectedContact.id}.pdf`,
        });
        this.onClose();
      } catch (error) {
        this.showAlert(this.$t('EXPORT_CONVERSATIONS.STATE.DOWNLOAD_ERROR'));
      } finally {
        this.isDownloading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.export-conversations {
  label {
    @apply mb-4 block;
  }
}
.export-conversations__date-range {
  @apply mb-4;

  > * {
    @apply mb-2;
  }
}
.export-conversations__field-label {
  @apply mb-2 text-sm font-medium text-slate-700 dark:text-slate-100;
}
.export-conversations__option {
  @apply flex items-center mb-4;
}
.export-conversations__checkbox-label {
  @apply flex items-center gap-2 mb-0;

  input {
    @apply mb-0;
  }
}
.export-conversations__info-icon {
  @apply ml-1 flex-shrink-0 text-slate-400 dark:text-slate-500;
}
.export-conversations__state {
  @apply text-sm text-slate-600 dark:text-slate-300 my-4;
}
.export-conversations__list {
  @apply mb-4;
}
</style>

<style lang="scss">
// Unscoped on purpose: v-tooltip appends the tooltip bubble outside this
// component's DOM tree, so scoped styles can't reach it. The modal itself
// sits at z-[9990] (see widgets/_modal.scss), which is well above the
// default .tooltip z-40, so without this override the tooltip renders but
// stays hidden behind the modal.
.export-conversations__tooltip {
  z-index: 9999 !important;
}
</style>
